import { WinnerAnnouncements } from '@/features/prizes/ui/WinnerAnnouncements'
import { FeedExperience } from '@/features/social-feed/ui/FeedExperience'
import { Screen } from '@/shared/ui/Layout'

import { getBlockComponent } from './blocks'
import { HomeSkeleton } from './components/HomeSkeleton'
import { useHomeBlocks } from './hooks/useHomeBlocks'
import { useHomeFeedMode } from './hooks/useHomeFeedMode'
import { useHomeView } from './hooks/useHomeView'

/**
 * A home é composta pelo produtor: uma lista ordenada de blocos vinda do
 * snapshot versionado. Sem blocos ativos ela renderiza vazia — não há fallback
 * para a composição fixa que existia antes.
 *
 * Modo `feed` (`useHomeFeedMode`) é exceção a essa composição: quando
 * `social_feed` é o único bloco ativo e renderizável, a home vira a própria
 * experiência do feed (`FeedExperience`) em vez de iterar `blocks` — não há
 * `HomeSkeleton`/espera por `useHomeView` nesse caminho porque
 * `FeedExperience` já tem seu próprio loading interno, e jornada/missões não
 * têm nenhum papel numa home que só é feed.
 *
 * `WinnerAnnouncements` fica **acima** da composição, nos dois caminhos, e por
 * isso não é um bloco do registro: a composição é do Produtor, e um aviso
 * dirigido ao participante não pode depender de ele tê-lo incluído — nem
 * desaparecer no modo que não itera bloco nenhum.
 */
export const DigitalProductHome = () => {
  const { isLoading } = useHomeView()
  const { blocks } = useHomeBlocks()
  const mode = useHomeFeedMode()

  if (mode === 'feed') {
    return (
      <Screen>
        <Screen.Context className="pt-2">
          <WinnerAnnouncements />
        </Screen.Context>
        <FeedExperience />
      </Screen>
    )
  }

  return (
    <Screen>
      <Screen.Content className="pt-2">
        <WinnerAnnouncements />

        {isLoading ? (
          <HomeSkeleton />
        ) : (
          <div className="flex flex-col gap-3 pb-8" data-testid="home-blocks">
            {blocks.map(block => {
              const Component = getBlockComponent(block.kind)
              // `kind` desconhecido não derruba os demais blocos.
              if (!Component) return null

              return <Component key={block.id} block={block} />
            })}
          </div>
        )}
      </Screen.Content>
    </Screen>
  )
}

DigitalProductHome.pageTitle = 'Início'
